/**
 * Parent-facing quick-log tiles.
 *
 * The tile is two controls, and which is which matters. The big one logs the
 * default amount in one tap and is the entire reason this feature works — if
 * logging a $6 ice cream takes longer than about fifteen seconds the gauge
 * goes stale, and a gauge the family knows is wrong is worse than no gauge
 * (CODING_PLAN §14). The small one opens an amount field for the times the
 * default is wrong.
 *
 * The ordering is deliberate: the common case costs one tap and the uncommon
 * case costs two, rather than making every ice cream pay for the occasional
 * one that cost $8.50. Editing is a separate button rather than a long-press
 * because a gesture with no tap or keyboard equivalent is unreachable for
 * anyone not using a touchscreen.
 *
 * Corrections after the fact are an undo that writes a visible reversal, never
 * an edit — see `undoSpend`.
 */

import { useEffect, useRef, useState } from 'react';
import { centsFromDollars, formatCents } from '../domain/money.js';
import { TileIcon } from './Art.jsx';
import './QuickLog.css';

function PencilIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M11.2 2.3 13.7 4.8 5.5 13H3v-2.5l8.2-8.2Z" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * The one-off with no tile behind it.
 *
 * A tile set is a list of the things a family buys often, and it earns its
 * speed by being short — so the parking meter and the school cake sale have to
 * go somewhere that isn't "make a tile for it". Without this, they either go
 * on the nearest wrong tile or don't get logged, and both end with the gauge
 * disagreeing with the bank.
 */
const MANUAL = { id: '__manual__', label: 'Something else', defaultAmountCents: null };

/** The amount field a tile turns into. Pre-filled, pre-selected, one enter away. */
function AmountEntry({ tile, withNote, onLog, onCancel }) {
  // A tile knows what it usually costs. "Something else" by definition doesn't,
  // and a pre-filled 0.00 is a number to delete before typing rather than a
  // hint — so it opens empty.
  const [dollars, setDollars] = useState(
    tile.defaultAmountCents == null ? '' : (tile.defaultAmountCents / 100).toFixed(2),
  );
  const [note, setNote] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    // Focused and selected so the parent can type over it rather than clearing
    // it first. autoFocus is justified here: the control exists only because
    // they just asked to type a number into it.
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const cents = centsFromDollars(dollars);

  function submit(e) {
    e.preventDefault();
    // The note is optional on purpose. Requiring a description would put a
    // second field between a parent and a logged spend, and an unnamed amount
    // in the history is still a true one — the balance is what the gauge is
    // for.
    if (cents > 0) onLog(cents, note.trim() || undefined);
  }

  return (
    <form className="tile tile--editing" onSubmit={submit}>
      <label className="tile__label">
        {tile.label}
        <input
          ref={inputRef}
          type="number" inputMode="decimal" min="0" step="0.01"
          value={dollars}
          onChange={(e) => setDollars(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') onCancel(); }}
          aria-label={`Amount in dollars for ${tile.label}`}
        />
      </label>
      {withNote && (
        <label className="tile__label small">
          What was it?
          <input
            value={note}
            maxLength={80}
            placeholder="Optional"
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') onCancel(); }}
          />
        </label>
      )}
      <div className="tile__actions">
        <button type="submit" className="primary" disabled={cents <= 0}>Log</button>
        <button type="button" className="quiet" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

export default function QuickLog({ tiles, onLog, onUndo, onManage, disabled }) {
  const [busyTile, setBusyTile] = useState(null);
  const [editing, setEditing] = useState(null);
  const [lastEntry, setLastEntry] = useState(null);
  const [error, setError] = useState(null);

  // `tile` is null for a one-off with no tile behind it; `note` is only ever
  // set on that path.
  async function log(tile, amountCents, note) {
    setBusyTile(tile ? tile.id : MANUAL.id);
    setEditing(null);
    setError(null);
    try {
      const entry = await onLog(tile, amountCents, note);
      setLastEntry({ entry, tile, amountCents, note });
    } catch (e) {
      setError(`Couldn’t log that — ${e.message}. Tap the tile to try again.`);
    }
    setBusyTile(null);
  }

  async function undo() {
    const pending = lastEntry;
    setLastEntry(null);
    try {
      await onUndo(pending.entry);
    } catch (e) {
      setError(`Couldn’t undo that — ${e.message}.`);
      setLastEntry(pending);
    }
  }

  // No early return on an empty tile set. A family that archived the last tile
  // still has to be able to log the ice cream they just bought, and the way
  // back to making a tile is on this section's header — a screen that renders
  // nothing is a dead end that looks like a bug.
  return (
    <section className="card stack" aria-labelledby="quicklog-heading">
      <div className="spread">
        <p className="eyebrow" id="quicklog-heading">Log a spend</p>
        {onManage && (
          <button className="quiet small" onClick={onManage}>Edit tiles</button>
        )}
      </div>

      <div className="tiles">
        {tiles.map((tile) => (
          editing === tile.id ? (
            <AmountEntry
              key={tile.id}
              tile={tile}
              onLog={(cents) => log(tile, cents)}
              onCancel={() => setEditing(null)}
            />
          ) : (
            <div className="tile" key={tile.id} aria-busy={busyTile === tile.id}>
              <button
                className="tile__log"
                onClick={() => log(tile, tile.defaultAmountCents)}
                disabled={disabled || busyTile === tile.id}
              >
                <TileIcon tile={tile} />
                <span className="tile__label">{tile.label}</span>
                <span className="tile__amount">{formatCents(tile.defaultAmountCents)}</span>
              </button>
              <button
                className="tile__edit"
                onClick={() => setEditing(tile.id)}
                disabled={disabled || busyTile === tile.id}
                aria-label={`Log a different amount for ${tile.label}`}
              >
                <PencilIcon />
              </button>
            </div>
          )
        ))}

        {/*
          Last, always. It is the slowest control here — two taps, a number to
          type, and nothing pre-filled — so it sits after the one-tap tiles
          rather than competing with them for the same glance.
        */}
        {editing === MANUAL.id ? (
          <AmountEntry
            tile={MANUAL}
            withNote
            onLog={(cents, note) => log(null, cents, note)}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <div className="tile tile--other" aria-busy={busyTile === MANUAL.id}>
            <button
              className="tile__log"
              onClick={() => setEditing(MANUAL.id)}
              disabled={disabled || busyTile === MANUAL.id}
            >
              <span className="tile__label">{MANUAL.label}</span>
              <span className="tile__amount">Type an amount</span>
            </button>
          </div>
        )}
      </div>

      {error && <p className="notice error" role="alert">{error}</p>}

      {lastEntry && (
        <div className="undo" aria-live="polite">
          <span>
            Logged {lastEntry.tile?.label ?? lastEntry.note ?? 'Spend'} —{' '}
            {formatCents(lastEntry.amountCents)}
          </span>
          <button onClick={undo}>Undo</button>
        </div>
      )}
    </section>
  );
}
